import { css, cx } from "@emotion/css";
import type { IconButtonElement } from "@octopusdeploy/design-system-components";
import { LegacyPopover, scrollbarStyles, IconButton, MenuItemButton, MenuList } from "@octopusdeploy/design-system-components";
import { CaretDownIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
type DropdownMenuOptionVariant = "danger";
export interface DropdownMenuOption {
    value: string | undefined | null;
    text: string;
    icon?: JSX.Element;
    disabled?: boolean;
    getDisabledText?: (text: string) => string;
    onChange?: () => void;
    variant?: DropdownMenuOptionVariant;
}
const controlStyles = {
    root: css({
        cursor: "pointer",
        height: "100%",
        position: "relative",
        width: "100%",
    }),
    disabled: css({
        cursor: "not-allowed",
    }),
};
const labelStyles = {
    error: css({
        color: themeTokens.color.textField.text.primary,
    }),
    open: css({
        opacity: 0,
    }),
    disabled: css({
        color: themeTokens.color.textField.text.disabled,
    }),
};
const dropdownMenuStyles = {
    root: css({
        display: "block",
        font: text.body.regular.medium,
        outline: "none",
        position: "relative",
        flexGrow: 1,
    }),
    buttonAndSelectedItemContainer: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    }),
    open: css({
        opacity: 1,
    }),
    error: css({
        borderBottomColor: themeTokens.color.textField.border.danger,
        borderBottomWidth: "2px",
        "&:hover": {
            borderBottomColor: themeTokens.color.textField.border.danger,
        },
    }),
};
const buttonStyles = css({
    position: "absolute",
    right: 0,
    display: "flex",
    alignItems: "center",
    verticalAlign: "middle",
});
const inlineButtonStyles = css({
    margin: `0 ${space[8]}`,
    padding: `0 ${space[8]} ${space[4]}`,
});
const withDivider = css({
    borderTop: `1px solid ${themeTokens.color.menuList.border.primary}`,
    paddingTop: space[12],
});
const stylesheet = {
    visuallyHidden: css({
        clip: "rect(0 0 0 0)",
        clipPath: "inset(50%)",
        height: "1px",
        overflow: "hidden",
        position: "absolute",
        whiteSpace: "nowrap",
        width: "1px",
    }),
    empty: css({
        padding: "1rem",
    }),
    menuListScrollContainer: css({
        minWidth: "150px",
        maxHeight: "400px",
        overflowY: "auto",
    }),
};
interface DeploymentsDropDownMenuProps {
    /**
     * Disables the menu.
     */
    disabled: boolean;
    /**
     * Callback function fired when a menu item is clicked, other than the one currently selected.
     * @param {string} value If `multiple` is true, the menu"s `value`
     * array with either the menu item"s `value` added (if
     * it wasn"t already selected) or omitted (if it was already selected).
     * Otherwise, the `value` of the menu item.
     */
    onChange: (value: string) => void;
    /**
     * Callback function fired when a menu item is clicked, other than the one currently selected.
     * @param {string} The `value` of the menu item.
     * @param {DropdownMenuOption} menuItem The selected `MenuItemButton`.
     * If `multiple` is true, this will be an array with the `MenuItemButton`s matching the `value`s parameter.
     */
    selectionRenderer: (value: string, menuItem: DropdownMenuOption) => React.ReactNode;
    menuItemRenderer: (menuItem: DropdownMenuOption) => React.ReactNode;
    value: string | undefined;
    /**
     * If provided, adds a value to a visually hidden and accessible input
     * as an alternative to the display value
     */
    items: DropdownMenuOption[];
}
interface DropDownMenuState {
    open: boolean;
    anchorEl: HTMLElement | null;
}
export class DeploymentsDropDownMenu extends React.Component<DeploymentsDropDownMenuProps, DropDownMenuState> {
    static muiName = "DropDownMenu";
    static defaultProps: Partial<DeploymentsDropDownMenuProps> = {
        disabled: false,
        items: [],
    };
    state: DropDownMenuState = {
        open: false,
        anchorEl: null,
    };
    componentDidMount() {
        this.close();
    }
    rootNode = React.createRef<HTMLDivElement>();
    downArrowIconButton = React.createRef<IconButtonElement>();
    firstMenuNode = React.createRef<HTMLButtonElement>();
    filterContainerRef = React.createRef<HTMLDivElement>();
    handleTouchTapControl = (event: React.MouseEvent) => {
        event.preventDefault();
        if (!this.props.disabled) {
            this.setState((prevState) => ({
                open: !prevState.open,
                anchorEl: this.rootNode.current,
            }));
        }
    };
    handleKeyDown = (event: React.KeyboardEvent<{}>) => {
        switch (event.key) {
            case "ArrowUp":
            case "ArrowDown":
                event.preventDefault();
                event.stopPropagation();
                this.setState({
                    open: true,
                    anchorEl: this.rootNode.current,
                });
                break;
        }
    };
    close = () => {
        this.setState({
            open: false,
        }, () => {
            requestAnimationFrame(() => {
                this.downArrowIconButton.current?.focus();
            });
        });
    };
    handleCloseMenuWhenPressingTab: React.KeyboardEventHandler = (event) => {
        if (event.key === "Tab") {
            this.close();
        }
    };
    render() {
        const { selectionRenderer, menuItemRenderer, value, onChange, items } = this.props;
        const { anchorEl, open } = this.state;
        const currentOption = items.find((x) => x.value === value);
        const displayValue: React.ReactNode = currentOption && currentOption.value ? selectionRenderer(currentOption.value, currentOption) : (currentOption?.text ?? currentOption?.value);
        let menuStyle = undefined;
        if (anchorEl) {
            const filterContainerRefWidth = this.filterContainerRef.current?.clientWidth ?? 0;
            const largestWidth = anchorEl.clientWidth > filterContainerRefWidth ? anchorEl.clientWidth : filterContainerRefWidth;
            menuStyle = Object.assign({
                width: largestWidth,
            });
        }
        const accessibleName = "drop down menu";
        return (<div ref={this.rootNode} className={cx(dropdownMenuStyles.root, { [dropdownMenuStyles.open]: open })}>
                <div className={controlStyles.root} onClick={this.handleTouchTapControl}>
                    <div className={dropdownMenuStyles.buttonAndSelectedItemContainer}>
                        <div className={cx({ [labelStyles.open]: open })}>{displayValue}</div>
                        <input type="text" readOnly className={stylesheet.visuallyHidden} value={""} tabIndex={-1}/>
                        <div className={buttonStyles}>
                            <div onKeyDown={this.handleKeyDown}>
                                <IconButton customIcon={<CaretDownIcon size={24}/>} ref={this.downArrowIconButton} accessibleName={"ToggleDropDown"}/>
                            </div>
                        </div>
                    </div>
                </div>
                <LegacyPopover anchorEl={anchorEl} open={open} onClose={this.close}>
                    <div onKeyDown={this.handleCloseMenuWhenPressingTab} tabIndex={-1} className={cx(stylesheet.menuListScrollContainer, scrollbarStyles)} style={menuStyle}>
                        <MenuList accessibleName={accessibleName}>
                            {items.map((menuItem, index) => {
                const refProps = index === 0 ? { ref: this.firstMenuNode } : {};
                return (<MenuItemButton key={menuItem.value ?? index} {...refProps} onClick={() => {
                        onChange?.(menuItem.value ?? "");
                        this.close();
                        menuItem.onChange?.();
                    }} isSelected={menuItem.value === this.props.value} autoFocus={(index === 0 && !Boolean(value)) || menuItem.value === value} compact={true}>
                                        {menuItemRenderer?.(menuItem)}
                                    </MenuItemButton>);
            })}
                        </MenuList>
                    </div>
                </LegacyPopover>
            </div>);
    }
    static displayName = "DeploymentsDropDownMenu";
}
